import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"
import * as React from "react"

import { cn } from "@/lib/utils"

/**
 * Кнопка приборной панели: невысокая, с почти нулевым радиусом, надпись —
 * конденсированным шрифтом в разрядку. Вариантов ровно столько, сколько ролей
 * на экране: главное действие (`primary`), обычное (`default`), тихое
 * (`ghost`), опасное (`danger` — пока не используется, но оно единственное
 * место, где вообще допустим красный: `decline` означает исход скоринга).
 */
const buttonVariants = cva(
  "inline-flex select-none items-center justify-center gap-1.5 whitespace-nowrap rounded border font-medium transition-colors duration-150 ease-snap disabled:pointer-events-none disabled:opacity-45",
  {
    variants: {
      variant: {
        default:
          "border-line-strong bg-surface text-fg shadow-panel hover:bg-surface-sunken active:bg-surface-sunken",
        primary:
          "border-accent bg-accent text-accent-fg shadow-raised hover:bg-accent/90 active:bg-accent",
        ghost: "border-transparent bg-transparent text-fg-muted hover:bg-surface-sunken hover:text-fg",
        danger: "border-decline bg-decline text-white hover:bg-decline/90",
      },
      size: {
        sm: "h-7 px-2.5 text-[13px]",
        md: "h-8 px-3 text-[13px]",
        lg: "h-9 px-4 text-sm",
        icon: "h-8 w-8 p-0",
      },
    },
    defaultVariants: { variant: "default", size: "md" },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Component = asChild ? Slot : "button"
    return (
      <Component
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = "Button"

export { buttonVariants }
